import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { utils, writeFile } from "xlsx";

interface PersonalPorPuestoProps {
  staffByPosition: any;
}

const PersonalPorPuesto: React.FC<PersonalPorPuestoProps> = ({
  staffByPosition,
}) => {
  const [showAllPositions, setShowAllPositions] = useState(false);

  if (!staffByPosition || !staffByPosition.byPosition?.length) {
    return null;
  }

  return (
    <div
      style={{
        ...styles.chartCard,
        flex: 1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={styles.chartHeader}>
        <div style={styles.chartHeaderContent}>
          <h3 style={styles.chartTitle}>
            <i
              className="fa-solid fa-users-gear"
              style={{ ...styles.chartTitleIcon, color: "#8b5cf6" }}
            ></i>
            Personal por Puesto
          </h3>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <div
              style={{
                background: "#f3f4f6",
                borderRadius: "8px",
                padding: "6px 12px",
                fontSize: "13px",
                color: "#374151",
                fontWeight: 500,
              }}
            >
              Total:{" "}
              <span style={{ color: "#8b5cf6", fontWeight: 700 }}>
                {staffByPosition.totals.activeCount}
              </span>{" "}
              activos
            </div>
            <button
              onClick={() => {
                const ws = utils.aoa_to_sheet([]);
                const headers = ["Puesto", "Activos", "Bajas", "Total"];
                utils.sheet_add_aoa(ws, [headers], { origin: "A1" });
                let rowIndex = 2;
                staffByPosition.byPosition.forEach((item: any) => {
                  const row = [
                    item.position.name,
                    item.activeCount,
                    item.inactiveCount,
                    item.totalCount,
                  ];
                  utils.sheet_add_aoa(ws, [row], {
                    origin: `A${rowIndex}`,
                  });
                  rowIndex++;
                });
                ws["!cols"] = [
                  { wch: 45 },
                  { wch: 12 },
                  { wch: 12 },
                  { wch: 12 },
                ];
                const wb = utils.book_new();
                utils.book_append_sheet(wb, ws, "Personal por Puesto");
                writeFile(wb, "Personal_Por_Puesto.xlsx");
              }}
              style={styles.exportChartButton}
              title="Descargar Excel"
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#16a34a";
                e.currentTarget.style.borderColor = "#16a34a";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#22c55e";
                e.currentTarget.style.borderColor = "#22c55e";
              }}
            >
              <i
                className="fa-solid fa-file-arrow-down"
                style={styles.exportChartIcon}
              ></i>
              <span style={styles.exportChartText}>Excel</span>
            </button>
          </div>
        </div>
      </div>
      <div
        style={{
          ...styles.chartBody,
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            maxHeight: "280px",
            overflowY: showAllPositions ? "auto" : "hidden",
            flex: 1,
          }}
        >
          <ResponsiveContainer
            width="100%"
            height={
              showAllPositions ? staffByPosition.byPosition.length * 40 : 280
            }
          >
            <BarChart
              data={
                showAllPositions
                  ? staffByPosition.byPosition
                  : staffByPosition.byPosition.slice(0, 7)
              }
              layout="vertical"
              margin={{ top: 10, right: 30, left: 180, bottom: 10 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#e5e7eb"
                horizontal={true}
                vertical={false}
              />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis
                type="category"
                dataKey="position.name"
                tick={{ fontSize: 11 }}
                width={170}
                tickFormatter={(value) =>
                  value.length > 28 ? value.substring(0, 28) + "..." : value
                }
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return (
                      <div style={styles.customTooltip}>
                        <p style={styles.tooltipLabel}>{data.position.name}</p>
                        <p style={{ color: "#8b5cf6", margin: "4px 0" }}>
                          Activos: {data.activeCount}
                        </p>
                        <p style={{ color: "#ef4444", margin: "4px 0" }}>
                          Bajas: {data.inactiveCount}
                        </p>
                        <p
                          style={{
                            color: "#374151",
                            margin: "4px 0",
                            fontWeight: 600,
                          }}
                        >
                          Total: {data.totalCount}
                        </p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Legend />
              <Bar
                dataKey="activeCount"
                name="Activos"
                fill="#8b5cf6"
                radius={[0, 4, 4, 0]}
              />
              <Bar
                dataKey="inactiveCount"
                name="Bajas"
                fill="#fbbf24"
                radius={[0, 4, 4, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {staffByPosition.byPosition.length > 7 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "12px",
              background: "#f9fafb",
              borderRadius: "8px",
              marginTop: "16px",
              fontSize: "13px",
              color: "#6b7280",
            }}
          >
            <div>
              <i
                className="fa-solid fa-info-circle"
                style={{ marginRight: "8px" }}
              ></i>
              {showAllPositions ? (
                <>
                  Mostrando <strong>todos</strong> los puestos (
                  {staffByPosition.byPosition.length}).
                  <span
                    style={{
                      marginLeft: "8px",
                      color: "#2563eb",
                      fontWeight: 500,
                    }}
                  >
                    <i
                      className="fa-solid fa-arrow-down"
                      style={{ marginRight: "4px" }}
                    ></i>
                    Desplázate para ver más
                  </span>
                </>
              ) : (
                <>
                  Mostrando los <strong>7</strong> puestos con más personal de{" "}
                  {staffByPosition.byPosition.length} en total
                </>
              )}
            </div>
            <button
              type="button"
              onClick={() => setShowAllPositions((prev) => !prev)}
              style={{
                border: "none",
                background: "transparent",
                color: "#2563eb",
                fontSize: "13px",
                fontWeight: 600,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <span>
                {showAllPositions ? "Ver solo top 7" : "Ver todos los puestos"}
              </span>
              <i
                className={
                  showAllPositions
                    ? "fa-solid fa-chevron-up"
                    : "fa-solid fa-chevron-down"
                }
                style={{ fontSize: "11px" }}
              ></i>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  chartCard: {
    background: "#fff",
    borderRadius: "16px",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
    overflow: "hidden",
  },
  chartHeader: {
    background: "linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)",
    padding: "20px 28px",
    borderBottom: "1px solid #e5e7eb",
  },
  chartHeaderContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
  },
  chartTitle: {
    margin: 0,
    fontSize: "18px",
    fontWeight: "600",
    color: "#2d3748",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flex: 1,
  },
  chartTitleIcon: {
    fontSize: "20px",
    color: "#667eea",
  },
  exportChartButton: {
    background: "#22c55e",
    border: "1px solid #22c55e",
    borderRadius: "8px",
    padding: "8px 14px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    transition: "all 0.2s ease",
    minWidth: "auto",
  },
  exportChartIcon: {
    color: "#fff",
    fontSize: "14px",
    transition: "color 0.2s ease",
  },
  exportChartText: {
    color: "#fff",
    fontSize: "13px",
    fontWeight: "600",
    margin: 0,
  },
  chartBody: {
    padding: "28px",
  },
  customTooltip: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    padding: "12px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  },
  tooltipLabel: {
    fontWeight: "600",
    marginBottom: "8px",
    color: "#2d3748",
  },
};

export default PersonalPorPuesto;
